import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MainLayout } from '@/layouts/MainLayout';
import { Alert, ProgressSpinner } from '@/components/ui';
import { usePowerBIStore } from '@/store/powerBIStore';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);

  return isMobile;
}

export function PowerBIReportPage() {
  const { reportId, type, endpoint } = useParams<{
    reportId: string;
    type: 'report' | 'dashboard';
    endpoint?: string;
  }>();

  const isMobile = useIsMobile();
  const { embedConfig, loading, error, generateEmbedToken } = usePowerBIStore();

  useEffect(() => {
    if (reportId && type) {
      generateEmbedToken(reportId, type, endpoint || 'generateembedtoken');
    }
  }, [reportId, type, endpoint, generateEmbedToken]);

  return (
    <MainLayout fullScreen>
      <div className="flex h-[calc(100vh-4rem)] w-full flex-col bg-white dark:bg-dark-900">
        {/* Error Alert */}
        {error && (
          <div className="absolute left-1/2 top-4 z-50 w-full max-w-md -translate-x-1/2">
            <Alert variant="error" title="Error loading report">
              {error}
            </Alert>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex h-full items-center justify-center">
            <ProgressSpinner size="lg" />
            <span className="ml-3 text-gray-600 dark:text-gray-300">Loading report...</span>
          </div>
        )}

        {/* Power BI Embed - Full Screen */}
        {embedConfig && !loading && (
          <div className="h-full w-full">
            <PowerBIEmbed
              embedConfig={{
                type: type || 'report',
                id: embedConfig.id,
                embedUrl: embedConfig.embedUrl,
                accessToken: embedConfig.accessToken,
                tokenType: models.TokenType.Embed,
                settings: {
                  filterPaneEnabled: false,
                  navContentPaneEnabled: true,
                  panes: {
                    filters: {
                      expanded: false,
                      visible: false,
                    },
                    pageNavigation: {
                      visible: true,
                      position: models.PageNavigationPosition.Left,
                    },
                  },
                  background: models.BackgroundType.Default,
                  layoutType: isMobile
                    ? models.LayoutType.MobilePortrait
                    : models.LayoutType.Master,
                },
              }}
              cssClassName="h-full w-full"
              eventHandlers={
                new Map([
                  ['loaded', () => console.log('Report loaded')],
                  ['rendered', () => console.log('Report rendered')],
                  [
                    'error',
                    (event: unknown) => {
                      console.error('Report error:', event);
                    },
                  ],
                ])
              }
            />
          </div>
        )}
      </div>
    </MainLayout>
  );
}
